import Accordion from 'components/Accordion';
import ErrorBoundary from 'components/ErrorBoundary';
import { LayoutContainer } from 'components/Layout';
import { TechnicalSpecification } from 'components/Product';
import Text from 'components/Text';
import {
	PRODUCT_DETAILS_IMPORTANT_INFO_ID,
	PRODUCT_DETAILS_TECHNICAL_ATTRIBUTES_ID,
} from 'constants/ids';
import { useSafeFieldValues } from 'hooks';
import type { Product, VariantId } from 'models/product';
import { getVerticalEnergyLabel } from 'utils/business-logic';
import { cn } from 'utils/classNames';
import { is } from 'utils/helpers';

import ExpiredProductInfo from './ExpiredProductInfo';
import {
	filterAdditionalTexts,
	hasChemicalInformation,
} from './helpers/productDataHelpers';
import ProductDetailsBrandInfo from './ProductDetailsBrandInfo';
import ProductDetailsBundleProducts from './ProductDetailsBundleProducts';
import type { AddCampaignData } from './ProductDetailsCampaign';
import ProductDetailsDescription from './ProductDetailsDescription';
import ProductDetailsExternalReview from './ProductDetailsExternalReview';
import ProductDetailsImportantInfo from './ProductDetailsImportantInfo';
import ProductDetailsIntro from './ProductDetailsIntro';
import ProductDetailsMedia from './ProductDetailsMedia';
import ProductDetailsSidebar from './ProductDetailsSidebar';
import ProductDetailsSimilarProducts from './ProductDetailsSimilarProducts';
import ProductQuestions from './ProductQuestions';
import {
	AccessoriesAccordion,
	BelongsToAccordion,
	BelongsToBundleAccordion,
	SparePartsAccordion,
} from './RelatedProductsAccordion';
import ReviewSummary from './Reviews';

export interface Props {
	isAboutBrandOpen: boolean;
	isImportantInfoOpen: boolean;
	isTechnicalAttributesOpen: boolean;
	onAboutBrandClick: () => void;
	onCampaignPackageAddToCartClick:
		| ((campaign: AddCampaignData) => Promise<void>)
		| undefined;
	onCampaignPackageAddToWishlistClick:
		| ((campaign: AddCampaignData) => Promise<void>)
		| undefined;
	onCreditSimulationClick: () => void;
	onImportantInfoClick: () => void;
	onMainPurchaseButtonClick: () => void;
	onMainWishlistButtonClick: () => void;
	onManufacturerInformationClick: () => void;
	onSafetyInstructionsClick: () => void;
	onSelectedAccessoryUpsellAdd: (id: VariantId) => void;
	onSelectedAccessoryUpsellRemove: (id: VariantId) => void;
	onSizeGuideClick: () => void;
	onTechnicalAttributesClick: () => void;
	product: Product;
	selectedAccessoryUpsellIds: VariantId[];
}

export default function ProductDetails({
	isAboutBrandOpen,
	isImportantInfoOpen,
	isTechnicalAttributesOpen,
	onAboutBrandClick,
	onCampaignPackageAddToCartClick,
	onCampaignPackageAddToWishlistClick,
	onCreditSimulationClick,
	onImportantInfoClick,
	onMainPurchaseButtonClick,
	onMainWishlistButtonClick,
	onManufacturerInformationClick,
	onSafetyInstructionsClick,
	onSelectedAccessoryUpsellAdd,
	onSelectedAccessoryUpsellRemove,
	onSizeGuideClick,
	onTechnicalAttributesClick,
	product,
	selectedAccessoryUpsellIds,
}: Props) {
	const {
		brand,
		externalTestReview,
		hasAccessories,
		hasBelongsTo,
		hasBelongsToBundle,
		hasSpareParts,
		id,
		packageSize,
		reviewScore,
		salesAttributes,
	} = product;

	const safeContent = useSafeFieldValues(product.content);

	const additionalTexts = filterAdditionalTexts(product.additionalTexts);
	const documents = product.otherAssets?.filter(
		(asset) => asset.type === 'ProductDocument',
	);
	const verticalEnergyLabel = getVerticalEnergyLabel(product);
	const technicalCategories = product.technicalAttributes?.categories;
	const externalTestReviewLogo = externalTestReview?.image;

	const hasBrandInformation = Boolean(brand?.descriptionFormatted);
	const hasChemicalInfo = hasChemicalInformation(product);
	const hasImportantInformation =
		is.arrayWithLength(additionalTexts) || hasChemicalInfo;

	const contentClassName = 'max-w-200';

	return (
		<>
			<LayoutContainer
				innerClassName={cn(
					"[grid-template-areas:'a_a_a_a_a_a_a_a_a_a_a_a''b_b_b_b_b_b_b_._s_s_s_s''c_c_c_c_c_c_c_._s_s_s_s']",
					// Collapse the implicit a and b rows to min-content and let the c
					// row take the rest. This prevents whitespace from being distributed
					// between the rows if the sidebar is taller than the main content.
					'md:grid-rows-[min-content_min-content_1fr]',
					'md:grid md:grid-cols-12 md:gap-x-6',
				)}
			>
				{salesAttributes?.expired && (
					<ExpiredProductInfo
						className="md:hidden"
						expiredText={safeContent.expiredProductInfoText}
						viewSimilarProductsLinkText={
							safeContent.expiredProductInfoViewSimilarProductsLinkText
						}
					/>
				)}

				<Text
					as="h1"
					className="mb-2 [grid-area:a] max-md:mt-1"
					text={product.title}
				/>

				<div className="[grid-area:b]">
					<ProductDetailsIntro
						id={product.id}
						brand={brand}
						productModel={product.productModel}
						reviewScore={reviewScore}
						tags={product.productTags}
						variantModel={product.variantModel}
					/>
					<ProductDetailsMedia
						className="mt-4 md:mt-12"
						product={product}
						dialogLabel={safeContent.imagesDialogLabel}
						imagesSkipLinkText={safeContent.productImagesSkipLinkText}
						imageCarouselShowLargeImageButtonLabel={
							safeContent.imageCarouselShowLargeImageButtonLabel
						}
					/>
				</div>

				<div className="[grid-area:s] max-md:mt-4">
					<ErrorBoundary>
						{salesAttributes?.expired && (
							<ExpiredProductInfo
								className="max-md:hidden"
								expiredText={safeContent.expiredProductInfoText}
								viewSimilarProductsLinkText={
									safeContent.expiredProductInfoViewSimilarProductsLinkText
								}
							/>
						)}
						{!salesAttributes?.expired && (
							<ProductDetailsSidebar
								product={product}
								onEnergyLabelClick={onTechnicalAttributesClick}
								onMainPurchaseButtonClick={onMainPurchaseButtonClick}
								onWishlistButtonClick={onMainWishlistButtonClick}
								onSizeGuideClick={onSizeGuideClick}
								onCreditSimulationClick={onCreditSimulationClick}
								selectedAccessoryUpsellIds={selectedAccessoryUpsellIds}
								onSelectedAccessoryUpsellAdd={onSelectedAccessoryUpsellAdd}
								onSelectedAccessoryUpsellRemove={
									onSelectedAccessoryUpsellRemove
								}
								onCampaignPackageAddToCartClick={
									onCampaignPackageAddToCartClick
								}
								onCampaignPackageAddToWishlistClick={
									onCampaignPackageAddToWishlistClick
								}
							/>
						)}
					</ErrorBoundary>
				</div>

				<div className="mt-4 [grid-area:c]">
					{externalTestReview?.result && externalTestReview.source && (
						<ProductDetailsExternalReview
							className="mb-r-xl max-w-132"
							features={externalTestReview?.features}
							heading={externalTestReview.result}
							logoAlt={externalTestReviewLogo?.alt}
							logoSrc={externalTestReviewLogo?.src}
							sourceLinkLabel={safeContent.externalReviewSourceLinkLabel}
							sourceText={safeContent.externalReviewSourceText}
							sourceUrl={externalTestReview?.sourceUrl}
							source={externalTestReview.source}
						/>
					)}

					<ProductDetailsDescription
						aboutSectionHeading={
							product.productType === 'Bundle'
								? safeContent.bundleDescriptionHeading
								: safeContent.productDescriptionHeading
						}
						bundleListHeading={safeContent.bundleDescriptionListHeading}
						product={product}
						descriptionClassName={contentClassName}
						onSafetyInstructionsClick={onSafetyInstructionsClick}
						showSafetyInstructionsButtonText={
							safeContent.showSafetyInstructionsButtonText
						}
					/>

					<Accordion
						className="mt-8"
						id={PRODUCT_DETAILS_TECHNICAL_ATTRIBUTES_ID}
						title={safeContent.technicalSpecificationSectionHeading}
						titleTag="h2"
						isOpen={isTechnicalAttributesOpen}
						onClick={onTechnicalAttributesClick}
					>
						<TechnicalSpecification
							className={cn(contentClassName, 'my-4')}
							categories={technicalCategories}
							documentsHeading={
								safeContent.technicalSpecificationDocumentsHeading
							}
							packageSize={packageSize}
							packageSizeHeading={
								safeContent.technicalSpecificationPackageSizeHeading
							}
							energyLabelSrc={verticalEnergyLabel?.src}
							energyLabelAlt={verticalEnergyLabel?.alt}
							documents={documents}
							manufacturerInformationButtonText={
								safeContent.technicalSpecificationManufacturerInformationPopoverButtonText
							}
							manufacturerInformationHeading={
								safeContent.technicalSpecificationManufacturerInformationHeading
							}
							onManufacturerInformationClick={onManufacturerInformationClick}
							hasManufacturerInfo={product.hasManufacturerInfo}
						/>
					</Accordion>
					<ProductQuestions
						askQuestionButtonText={
							safeContent.productQuestionsAskQuestionButtonText
						}
						feedbackDoneText={safeContent.reviewFeedbackDoneText}
						introText={safeContent.productQuestionsIntroText}
						loadMoreButtonText={safeContent.productQuestionsLoadMoreButtonText}
						popoverHeading={safeContent.productQuestionsPopoverTitle}
						reportButtonText={safeContent.reviewReportButtonText}
						title={safeContent.productQuestionsSectionHeading}
						upvoteButtonText={safeContent.reviewUpvoteButtonText}
						variantId={id}
						productName={product.title}
					/>

					{hasBelongsToBundle && (
						<BelongsToBundleAccordion
							variantId={product.id}
							contentClassName={contentClassName}
							expiredProductsTitle={
								safeContent.relatedProductsExpiredProductsListHeading
							}
							loadMoreButtonText={safeContent.belongsToBundleLoadMoreButtonText}
							noProductsText={safeContent.relatedProductsNoProductsText}
							title={safeContent.relatedProductsBelongsToBundleHeading}
						/>
					)}
					{hasAccessories && (
						<AccessoriesAccordion
							variantId={product.id}
							contentClassName={contentClassName}
							expiredProductsTitle={
								safeContent.relatedProductsExpiredProductsListHeading
							}
							loadMoreButtonText={safeContent.accessoriesLoadMoreButtonText}
							noProductsText={safeContent.relatedProductsNoProductsText}
							title={safeContent.relatedProductsAccessoriesHeading}
						/>
					)}
					{hasSpareParts && (
						<SparePartsAccordion
							variantId={product.id}
							contentClassName={contentClassName}
							expiredProductsTitle={
								safeContent.relatedProductsExpiredProductsListHeading
							}
							loadMoreButtonText={safeContent.sparePartsLoadMoreButtonText}
							noProductsText={safeContent.relatedProductsNoProductsText}
							title={safeContent.relatedProductsSparePartsHeading}
						/>
					)}
					{hasBelongsTo && (
						<BelongsToAccordion
							variantId={product.id}
							contentClassName={contentClassName}
							expiredProductsTitle={
								safeContent.relatedProductsExpiredProductsListHeading
							}
							loadMoreButtonText={safeContent.belongsToLoadMoreButtonText}
							noProductsText={safeContent.relatedProductsNoProductsText}
							title={safeContent.relatedProductsBelongsToHeading}
							belongsToListMessage={
								safeContent.relatedProductsBelongsToMayHaveMoreProductsInfoText
							}
						/>
					)}
					{hasBrandInformation && (
						<Accordion
							id="about-brand"
							title={safeContent.brandSectionHeading}
							titleTag="h2"
							isOpen={isAboutBrandOpen}
							onClick={onAboutBrandClick}
						>
							<ProductDetailsBrandInfo
								className={cn(contentClassName, 'mb-6 md:mb-6')}
								product={product}
								visitBrandPageButtonText={safeContent.visitBrandPageButtonText}
							/>
						</Accordion>
					)}
					{hasImportantInformation && (
						<Accordion
							id={PRODUCT_DETAILS_IMPORTANT_INFO_ID}
							title={safeContent.importantInformationSectionHeading}
							titleTag="h2"
							isOpen={isImportantInfoOpen}
							onClick={onImportantInfoClick}
						>
							<ProductDetailsImportantInfo
								className={contentClassName}
								hasChemicalStatements={hasChemicalInfo}
								product={product}
							/>
						</Accordion>
					)}
					<ReviewSummary
						className={cn(contentClassName, 'mt-r-4xl')}
						reviewScore={reviewScore}
						title={safeContent.reviewsSectionHeading}
						filterSortingHeading={safeContent.filterSortingHeading}
						loadMoreButtonText={safeContent.reviewsLoadMoreButtonText}
						openReviewPopoverButtonText={
							safeContent.openReviewsPopoverButtonText
						}
						ratingReviewCountText={safeContent.reviewsRatingReviewCountText}
						readReviewButtonText={safeContent.readReviewButtonText}
						reviewImagesDialogLabel={safeContent.reviewImagesDialogLabel}
						reviewOnlyWithImagesCheckboxLabel={
							safeContent.reviewOnlyWithImagesCheckboxLabel
						}
						reviewPolicyLinkText={safeContent.reviewPolicyLinkText}
						reviewPolicyText={safeContent.reviewPolicyText}
						reviewProductNotRecommendedText={
							safeContent.reviewProductNotRecommendedText
						}
						reviewProductRecommendedText={
							safeContent.reviewProductRecommendedText
						}
						reviewScoreFilterDefaultOptionLabel={
							safeContent.reviewScoreFilterDefaultOptionLabel
						}
						reviewScoreFilterLabel={safeContent.reviewScoreFilterLabel}
						reviewShowOriginalTextButtonText={
							safeContent.reviewShowOriginalTextButtonText
						}
						reviewShowTranslationToLanguageButtonText={
							safeContent.reviewShowTranslationToLanguageButtonText
						}
						reviewSortingDateAscendingOptionLabel={
							safeContent.reviewSortingDateAscendingOptionLabel
						}
						reviewSortingDateDescendingOptionLabel={
							safeContent.reviewSortingDateDescendingOptionLabel
						}
						reviewSortingDefaultOptionLabel={
							safeContent.reviewSortingDefaultOptionLabel
						}
						reviewSortingLabel={safeContent.reviewSortingLabel}
						reviewSortingRelevanceOptionLabel={
							safeContent.reviewSortingRelevanceOptionLabel
						}
						reviewSortingScoreAscendingOptionLabel={
							safeContent.reviewSortingScoreAscendingOptionLabel
						}
						reviewSortingScoreDescendingOptionLabel={
							safeContent.reviewSortingScoreDescendingOptionLabel
						}
						reviewsPopoverTitle={safeContent.reviewsPopoverTitle}
						reviewTranslatedFromLanguageText={
							safeContent.reviewTranslatedFromLanguageText
						}
						feedbackDoneText={safeContent.reviewFeedbackDoneText}
						flagAltText={safeContent.reviewFlagAltText}
						reportButtonText={safeContent.reviewReportButtonText}
						reviewAiSummaryDescription={safeContent.reviewAiSummaryDescription}
						reviewAiSummaryHeading={safeContent.reviewAiSummaryHeading}
						reviewImageButtonLabel={safeContent.reviewImageButtonLabel}
						reviewVerifiedBuyerLabel={safeContent.reviewVerifiedBuyerLabel}
						singleReviewPopoverTitle={safeContent.singleReviewPopoverTitle}
						upvoteButtonText={safeContent.reviewUpvoteButtonText}
						viewReviewImagesButtonText={safeContent.viewReviewImagesButtonText}
					/>
				</div>
			</LayoutContainer>

			<ProductDetailsBundleProducts
				heading={safeContent.bundleProductsSectionHeading}
				className="mt-r-3xl"
				productId={id}
			/>

			<ProductDetailsSimilarProducts
				heading={safeContent.similarProductsSectionHeading}
				loadMoreButtonText={safeContent.similarProductsLoadMoreButtonText}
				className="mt-r-3xl"
				productId={id}
			/>
		</>
	);
}
ProductDetails.displayName = 'ProductDetails';
